// src/invoiceprint.js
import React, { useEffect, useMemo, useState } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';


const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const asBool = (v, fallback = false) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v ?? '').trim().toLowerCase();
  if (!s) return fallback;
  if (['1', 'true', 'yes', 'y', 'on'].includes(s)) return true;
  if (['0', 'false', 'no', 'n', 'off'].includes(s)) return false;
  return fallback;
};

const sameText = (a, b) => {
  const x = String(a || '').trim().toLowerCase();
  const y = String(b || '').trim().toLowerCase();
  if (!x || !y) return false;
  return x === y;
};

const parseDate = (v) => {
  if (!v) return null;
  const s = String(v);
  const d = new Date(s.includes('T') ? s : `${s}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const fmtShortDate = (v) => {
  const d = parseDate(v);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy}`;
};

const getInvoiceDate = (inv) => {
  return inv?.invoice_date || inv?.invoiceDate || inv?.created_at || inv?.createdAt || inv?.date_created || inv?.dateCreated || null;
};

const getDueDate = (inv) => {
  return inv?.due_date || inv?.dueDate || inv?.due || null;
};

const normalizeBrandingRow = (row) => {
  const invoiceLayout = String(row?.invoiceLayout || row?.invoice_layout || 'modern').toLowerCase();
  return {
    invoiceLayout,
    logoUrl: row?.logoUrl || row?.logo || null,
    tagline: row?.tagline || '',
    primary_color: row?.primary_color || row?.primaryColor || '#000000',
    secondary_color: row?.secondary_color || row?.secondaryColor || '#374151',
  };
};

const InvoicePrint = ({ invoice, usePreviewBranding = false, docType = 'INVOICE' }) => {
  const [brandingRow, setBrandingRow] = useState(null);
  const [businessRow, setBusinessRow] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const inv = useMemo(() => invoice || {}, [invoice]);

  const isPreview =
    String(inv?.invoice_number || '').toUpperCase().startsWith('PREVIEW-') ||
    inv?.__isBrandingPreview === true;

  const publicBusinessRow = inv?.__publicBusinessSettings;
  const isPublicDocument =
    isPreview &&
    publicBusinessRow !== null &&
    typeof publicBusinessRow === 'object';

  const DOC = String(docType || 'INVOICE').toUpperCase();
  const DATE_LABEL = DOC === 'ESTIMATE' ? 'ESTIMATE DATE' : 'INVOICE DATE';

  // Branding
  useEffect(() => {
    if (isPreview) return;
    let alive = true;
    (async () => {
      try {
        const API = getApiBase();
        const res = await fetch(`${API}/api/settings/branding`, {
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        if (data && Object.keys(data).length) setBrandingRow(data);
      } catch {}
    })();
    return () => { alive = false; };
  }, [isPreview]);

  // Business settings (toggles + businessName + address)
  useEffect(() => {
    if (isPreview) return;
    let alive = true;
    (async () => {
      try {
        const API = getApiBase();
        const res = await fetch(`${API}/api/settings/business`, {
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        if (data && Object.keys(data).length) setBusinessRow(data);
      } catch {}
    })();
    return () => { alive = false; };
  }, [isPreview]);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const branding = useMemo(() => {
    if (isPreview) {
      const b = inv?.branding || inv?.branding_settings || inv?.brandingSettings || {};
      const invoiceLayout =
        b.invoiceLayout || b.invoice_layout || inv?.invoiceLayout || inv?.invoice_layout || 'modern';

      const primary =
        b.primary_color || b.primaryColor || inv?.primary_color || inv?.primaryColor || '#000000';

      const secondary =
        b.secondary_color || b.secondaryColor || inv?.secondary_color || inv?.secondaryColor || '#374151';

      return {
        invoiceLayout: String(invoiceLayout || 'modern').toLowerCase(),
        logoUrl: b.logoUrl || b.logo || inv?.logoUrl || null,
        tagline: b.tagline || inv?.tagline || '',
        primary_color: primary,
        secondary_color: secondary,
      };
    }

    if (brandingRow) return normalizeBrandingRow(brandingRow);

    const b = inv?.branding || inv?.branding_settings || inv?.brandingSettings || {};
    const invoiceLayout =
      b.invoiceLayout || b.invoice_layout || inv?.invoiceLayout || inv?.invoice_layout || 'modern';

    const primary =
      b.primary_color || b.primaryColor || inv?.primary_color || inv?.primaryColor || '#000000';

    const secondary =
      b.secondary_color || b.secondaryColor || inv?.secondary_color || inv?.secondaryColor || '#374151';

    return {
      invoiceLayout: String(invoiceLayout || 'modern').toLowerCase(),
      logoUrl: b.logoUrl || b.logo || inv?.logoUrl || null,
      tagline: b.tagline || inv?.tagline || '',
      primary_color: primary,
      secondary_color: secondary,
    };
  }, [brandingRow, inv, isPreview]);

  const business = useMemo(() => {
    // ✅ in preview mode, use __business* fields injected by BrandingSettings
    if (isPreview && inv.__businessName !== undefined) {
      const publicBusiness = isPublicDocument ? publicBusinessRow : {};
      const showTaxId = isPublicDocument
        ? asBool(
            DOC === 'ESTIMATE'
              ? publicBusiness.showTaxIdOnEstimate
              : publicBusiness.showTaxIdOnInvoice,
            false
          )
        : false;

      return {
        businessName: String(inv.__businessName || ''),
        address: String(inv.__businessAddress || ''),
        city: String(inv.__businessCity || ''),
        state: String(inv.__businessState || ''),
        zip: String(inv.__businessZip || ''),
        phone: String(inv.__businessPhone || ''),
        email: String(inv.__businessEmail || ''),
        website: String(inv.__businessWebsite || ''),
        taxId: isPublicDocument
          ? String(publicBusiness.taxId || '').trim()
          : '',
        businessLicense: isPublicDocument
          ? String(publicBusiness.businessLicense || '').trim()
          : '',
        insurancePolicy: isPublicDocument
          ? String(publicBusiness.insurancePolicy || '').trim()
          : '',
        showTaxId,
        showBusinessLicenseOnDocs: isPublicDocument
          ? asBool(publicBusiness.showBusinessLicenseOnDocs, false)
          : false,
        showInsurancePolicyOnDocs: isPublicDocument
          ? asBool(publicBusiness.showInsurancePolicyOnDocs, false)
          : false
      };
    }

    const row = businessRow || {};
    const businessName = String(row.businessName || row.business_name || row.name || '').trim();
    const taxId = String(row.taxId || '').trim();
    const businessLicense = String(row.businessLicense || '').trim();
    const insurancePolicy = String(row.insurancePolicy || '').trim();

    const showTaxIdOnInvoice = asBool(row.showTaxIdOnInvoice, false);
    const showTaxIdOnEstimate = asBool(row.showTaxIdOnEstimate, false);
    const showBusinessLicenseOnDocs = asBool(row.showBusinessLicenseOnDocs, false);
    const showInsurancePolicyOnDocs = asBool(row.showInsurancePolicyOnDocs, false);

    const showTaxId = (DOC === 'ESTIMATE') ? showTaxIdOnEstimate : showTaxIdOnInvoice;

    // subscriber address/contact (from business settings)
    const address = String(row.address || '').trim();
    const city = String(row.city || '').trim();
    const state = String(row.state || '').trim();
    const zip = String(row.zip || '').trim();
    const phone = String(row.phone || '').trim();
    const email = String(row.email || '').trim();
    const website = String(row.website || '').trim();

    return {
      businessName,
      address,
      city,
      state,
      zip,
      phone,
      email,
      website,

      taxId,
      businessLicense,
      insurancePolicy,
      showTaxId,
      showBusinessLicenseOnDocs,
      showInsurancePolicyOnDocs
    };
  }, [businessRow, DOC, isPreview, inv, isPublicDocument, publicBusinessRow]);

  if (!invoice) return null;

  const items = Array.isArray(inv.items) ? inv.items : [];

  // CUSTOMER (Bill To)
  const company = inv.company || '';
  const contact = inv.customer_name || '';

  // SUBSCRIBER (Header "From" / logo block)
  const fromName = business.businessName || '';

  const subscriberCityLine = [business.city, business.state].filter(Boolean).join(', ');
  const subscriberZip = business.zip ? ` ${business.zip}` : '';
  const subscriberLine2 = (subscriberCityLine || business.zip) ? `${subscriberCityLine}${subscriberZip}`.trim() : '';

  const hasSubscriberAddress =
    !!fromName ||
    !!business.address ||
    !!subscriberLine2 ||
    !!business.phone ||
    !!business.email ||
    !!business.website;

  const SubscriberFromBlock = ({ compact = false }) => {
    if (!hasSubscriberAddress) return null;
    const line = { fontWeight: 900, fontSize: compact ? 12 : 13, lineHeight: 1.25 };
    const small = { fontWeight: 900, fontSize: 12, lineHeight: 1.25, opacity: 0.95 };
    return (
      <div style={{ marginTop: compact ? 6 : 10 }}>
        {fromName ? <div style={{ fontWeight: 900, fontSize: compact ? 14 : 16 }}>{fromName}</div> : null}
        {business.address ? <div style={line}>{business.address}</div> : null}
        {subscriberLine2 ? <div style={line}>{subscriberLine2}</div> : null}
        {business.phone ? <div style={small}>Phone: {business.phone}</div> : null}
        {business.email ? <div style={small}>Email: {business.email}</div> : null}
        {business.website ? <div style={small}>Website: {business.website}</div> : null}
      </div>
    );
  };

  // customer address (Bill To)
  const addr1 = pick(inv, ['address', 'address1', 'street', 'street1', 'billing_address', 'billingAddress', 'bill_to_address', 'billToAddress', 'customer_address']);
  const addr2 = pick(inv, ['address2', 'street2', 'apt', 'suite', 'unit']);
  const city = pick(inv, ['city', 'billing_city', 'billingCity']);
  const state = pick(inv, ['state', 'billing_state', 'billingState']);
  const zip = pick(inv, ['zip', 'zipcode', 'postal', 'postalCode', 'billing_zip', 'billingZip']);

  const subtotal = Number(inv.subtotal || 0);

  const parseDiscountAmount = (sel, subtotal) => {
    try {
      const s = String(sel || "0").trim();
      if (!s || s === "0") return 0;

      if (!s.includes(":")) {
        const v = Number(s);
        return Number.isFinite(v) ? v : 0;
      }

      const parts = s.split(":");
      const type = String(parts[0] || "fixed").toLowerCase();
      const rawVal = Number(parts[1] || 0);
      const sub = Number(subtotal || 0);
      const val = Number.isFinite(rawVal) ? rawVal : 0;

      const amount = (type === "percentage") ? (sub * (val / 100)) : val;
      return Number.isFinite(amount) ? amount : 0;
    } catch (e) {
      return 0;
    }
  };

  const discount = parseDiscountAmount(inv.discount, inv.subtotal);
  const total = Number(inv.total || 0);
  const paid = Number(inv.amount_paid || 0);
  const balance = Math.max(0, Number(inv.balance_due ?? (total - paid)));

  const stateTaxAmount = Number(inv.stateTaxAmount || 0);
  const countyTaxAmount = Number(inv.countyTaxAmount || 0);

  const notes = items.filter(i => i?.type === 'note' && (i?.description || '').trim());

  const invDate = fmtShortDate(getInvoiceDate(inv));
  const dueDate = fmtShortDate(getDueDate(inv));

  const LAYOUT = branding.invoiceLayout;

  const frame = {
    border:
      LAYOUT === 'bold' ? `8px solid ${branding.secondary_color}` :
      LAYOUT === 'classic' ? '2px solid #666' :
      '4px solid black',
    padding: LAYOUT === 'classic' ? '24px' : '18px',
    fontFamily: LAYOUT === 'classic' ? 'Georgia, serif' : 'Arial, sans-serif',
    color: 'black',
    background: 'white',
    maxWidth: '100%',
    overflowX: 'auto',
    boxSizing: 'border-box'
  };

  const divider = (thick = false) => (
    <div style={{ marginTop: 14, borderTop: LAYOUT === 'classic' ? '1px solid #999' : `${thick ? 6 : 3}px solid black` }} />
  );

  const logoInline = (maxW = 220, maxH = 90) => {
    if (!branding.logoUrl) return null;
    return (
      <img
        src={branding.logoUrl}
        alt="Logo"
        style={{
          maxWidth: maxW,
          maxHeight: maxH,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  };

  const taglineBlock = () => {
    if (!branding.tagline) return null;
    return (
      <div
        style={{
          fontWeight: LAYOUT === 'classic' ? 400 : 900,
          fontSize: 12,
          marginTop: 6,
          fontStyle: LAYOUT === 'classic' ? 'italic' : 'normal'
        }}
      >
        {branding.tagline}
      </div>
    );
  };

  // Bill To (customer) with de-dupe for exact matches
  const BillTo = () => {
    const hasCompany = String(company || '').trim().length > 0;
    const hasContact = String(contact || '').trim().length > 0;
    const showSecondLine = hasCompany && hasContact && !sameText(company, contact);

    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 6, textTransform: LAYOUT === 'bold' ? 'uppercase' : 'none', letterSpacing: LAYOUT === 'bold' ? 1 : 0 }}>
          {LAYOUT === 'classic' ? 'Bill To' : 'BILL TO'}
        </div>

        <div style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
          {company || contact || ''}
        </div>

        {showSecondLine ? (
          <div style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>{contact}</div>
        ) : null}

        {(addr1 || addr2 || city || state || zip) ? (
          <>
            {addr1 ? <div style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>{addr1}</div> : null}
            {addr2 ? <div style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>{addr2}</div> : null}
            {(city || state || zip) ? (
              <div style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
                {[city, state].filter(Boolean).join(', ')} {zip}
              </div>
            ) : null}
          </>
        ) : (
          <div style={{ fontWeight: 900, opacity: 0.7 }}>(No address on file)</div>
        )}
      </div>
    );
  };

  // Bottom business toggle block (only show lines when value exists)
  const showBusinessBlock =
    (!isPreview || isPublicDocument) &&
    (
      (business.showTaxId && business.taxId) ||
      (business.showBusinessLicenseOnDocs && business.businessLicense) ||
      (business.showInsurancePolicyOnDocs && business.insurancePolicy)
    );

  const BusinessInfoBlock = () => {
    if (!showBusinessBlock) return null;

    const lineStyle = { fontWeight: 900, fontSize: 12, lineHeight: 1.35 };

    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '3px solid black' }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>BUSINESS INFO</div>

        {(business.showTaxId && business.taxId) ? <div style={lineStyle}>Tax ID: {business.taxId}</div> : null}
        {(business.showBusinessLicenseOnDocs && business.businessLicense) ? <div style={lineStyle}>Business License: {business.businessLicense}</div> : null}
        {(business.showInsurancePolicyOnDocs && business.insurancePolicy) ? <div style={lineStyle}>Insurance Policy: {business.insurancePolicy}</div> : null}
      </div>
    );
  };

  // HEADERS
  const HeaderClassic = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
      <div>
        <div style={{ fontWeight: 900, fontSize: 28, borderBottom: '2px solid #333', paddingBottom: 6, marginBottom: 12 }}>{DOC === 'ESTIMATE' ? 'Estimate' : 'Invoice'}</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#666', marginBottom: 2 }}>{DOC === 'ESTIMATE' ? 'Estimate Number' : 'Invoice Number'}</div>
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>#{inv.invoice_number}</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#666', marginBottom: 2 }}>{DATE_LABEL}</div>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{invDate || '—'}</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#666', marginBottom: 2 }}>Due Date</div>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{dueDate || '—'}</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#666', marginBottom: 2 }}>Status</div>
        <div style={{ fontWeight: 900 }}>{String(inv.status || 'unpaid').toUpperCase()}</div>
      </div>

      <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: isMobile ? 0 : 260, width: isMobile ? '100%' : undefined }}>
        {branding.logoUrl ? (
          <div style={{ display: 'inline-block', border: '1px solid #999', padding: 12, background: '#fafafa' }}>
            {logoInline(200, 80)}
          </div>
        ) : null}
        {taglineBlock()}
        {/* ✅ subscriber address/contact */}
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <SubscriberFromBlock compact />
        </div>

        <div style={{ border: '2px solid #666', padding: 14, marginTop: 12, background: '#f5f5f5' }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#666' }}>Amount Due</div>
          <div style={{ fontWeight: 900, fontSize: 'clamp(16px, 5vw, 26px)', marginTop: 4, color: '#000', whiteSpace: 'nowrap' }}>{money(balance)}</div>
          <div style={{ borderTop: '1px solid #ccc', marginTop: 10, paddingTop: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Total: {money(total)}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Paid: {money(paid)}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const HeaderModern = () => (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 14px',
          border: '4px solid black',
          background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`,
          color: 'white',
          fontWeight: 900,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ fontSize: 20, letterSpacing: 2 }}>{DOC}</div>
        <div style={{ fontSize: 14 }}>#{inv.invoice_number}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginTop: 12, flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ minWidth: isMobile ? 0 : 260, width: isMobile ? '100%' : undefined }}>
          {branding.logoUrl ? (
            <div style={{ display: 'inline-block', border: `4px solid ${branding.secondary_color}`, padding: 8, background: 'white' }}>
              {logoInline(220, 90)}
            </div>
          ) : null}
          {taglineBlock()}
          {/* ✅ subscriber name/address */}
          <SubscriberFromBlock />
        </div>

        <div style={{ minWidth: isMobile ? 0 : 280, width: isMobile ? '100%' : undefined }}>
          <div style={{ border: '4px solid black', padding: 12, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900 }}>
              <div>{DATE_LABEL}</div>
              <div>{invDate || '—'}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, marginTop: 6 }}>
              <div>DUE DATE</div>
              <div>{dueDate || '—'}</div>
            </div>
            <div style={{ marginTop: 10, borderTop: '3px solid black', paddingTop: 10 }}>
              <div style={{ fontWeight: 900, fontSize: 18 }}>TOTAL: {money(total)}</div>
              <div style={{ fontWeight: 900 }}>PAID: {money(paid)}</div>
              <div style={{ fontWeight: 900, color: branding.primary_color, fontSize: 20, marginTop: 4 }}>BALANCE: {money(balance)}</div>
            </div>
          </div>

          <div style={{ fontWeight: 900, marginTop: 10 }}>
            STATUS: {String(inv.status || 'unpaid').toUpperCase()}
          </div>
        </div>
      </div>
    </>
  );

  const HeaderProfessional = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
      <div style={{ minWidth: 260 }}>
        <div style={{ fontWeight: 900, fontSize: 26, borderLeft: `12px solid ${branding.primary_color}`, paddingLeft: 12, lineHeight: 1.1 }}>
          {DOC}
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {branding.logoUrl ? (
            <div style={{ border: '3px solid #ddd', padding: 8, background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              {logoInline(180, 70)}
            </div>
          ) : null}
          <div>
            {taglineBlock()}
            {/* ✅ subscriber name/address */}
            <SubscriberFromBlock compact />
          </div>
        </div>
      </div>

      <div style={{ minWidth: isMobile ? 0 : 300, width: isMobile ? '100%' : undefined }}>
        <div style={{ border: '3px solid #ddd', padding: 14, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900 }}>
            <div>{DOC === 'ESTIMATE' ? 'Estimate #' : 'Invoice #'}</div><div>{inv.invoice_number}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, marginTop: 6 }}>
            <div>{DATE_LABEL}</div><div>{invDate || '—'}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 900, marginTop: 6 }}>
            <div>Due Date</div><div>{dueDate || '—'}</div>
          </div>

          <div style={{ marginTop: 12, background: branding.secondary_color, color: 'white', border: '3px solid black', padding: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>TOTAL: {money(total)}</div>
            <div style={{ fontWeight: 900 }}>PAID: {money(paid)}</div>
            <div style={{ fontWeight: 900, fontSize: 20, marginTop: 4 }}>BALANCE: {money(balance)}</div>
          </div>
        </div>

        <div style={{ fontWeight: 900, marginTop: 10 }}>
          Status: {String(inv.status || 'unpaid').toUpperCase()}
        </div>
      </div>
    </div>
  );

  const HeaderBold = () => (
    <>
      <div
        style={{
          border: '6px solid black',
          padding: '16px 18px',
          background: branding.secondary_color,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 32, letterSpacing: 3 }}>{DOC}</div>
        <div style={{ fontWeight: 900, fontSize: 18 }}>#{inv.invoice_number}</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginTop: 14, flexDirection: isMobile ? 'column' : 'row' }}>
        <div style={{ minWidth: isMobile ? 0 : 260, width: isMobile ? '100%' : undefined }}>
          {branding.logoUrl ? (
            <div style={{ border: `8px solid ${branding.primary_color}`, padding: 12, background: 'white' }}>
              {logoInline(220, 90)}
            </div>
          ) : null}
          {taglineBlock()}
          {/* ✅ subscriber name/address */}
          <SubscriberFromBlock compact />

          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>{DATE_LABEL}</div>
            <div style={{ fontWeight: 900, fontSize: 18 }}>{invDate || '—'}</div>
            <div style={{ fontWeight: 900, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginTop: 8 }}>Due Date</div>
            <div style={{ fontWeight: 900, fontSize: 18, color: branding.primary_color }}>{dueDate || '—'}</div>
          </div>
        </div>

        <div style={{ minWidth: isMobile ? 0 : 320, width: isMobile ? '100%' : undefined }}>
          <div style={{ border: '6px solid black', padding: 16, background: '#fff' }}>
            <div style={{ fontWeight: 900, fontSize: 16, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
              Payment Summary
            </div>
            <div style={{ borderTop: '6px solid black', paddingTop: 12 }}>
              <div style={{ fontWeight: 900, fontSize: 'clamp(16px, 5vw, 28px)', color: branding.primary_color, whiteSpace: 'nowrap' }}>{money(total)}</div>
              <div style={{ fontWeight: 900, fontSize: 14, marginTop: 6 }}>PAID: {money(paid)}</div>
              <div style={{ fontWeight: 900, fontSize: 20, marginTop: 6, color: branding.secondary_color }}>BALANCE: {money(balance)}</div>
            </div>
          </div>
          <div style={{ fontWeight: 900, marginTop: 10, fontSize: 16, letterSpacing: 1 }}>
            STATUS: {String(inv.status || 'unpaid').toUpperCase()}
          </div>
        </div>
      </div>
    </>
  );

  const Header = () => {
    if (LAYOUT === 'classic') return <HeaderClassic />;
    if (LAYOUT === 'professional') return <HeaderProfessional />;
    if (LAYOUT === 'bold') return <HeaderBold />;
    return <HeaderModern />;
  };

  const tableHeaderBg =
    LAYOUT === 'classic' ? '#f5f5f5' :
    LAYOUT === 'professional' ? branding.secondary_color :
    LAYOUT === 'bold' ? branding.primary_color :
    branding.primary_color;

  const tableHeaderColor =
    (LAYOUT === 'professional' || LAYOUT === 'bold' || LAYOUT === 'modern') ? 'white' : 'black';

  return (
    <div style={frame}>
      <Header />

      {divider(LAYOUT === 'bold')}

      <BillTo />

      {divider()}

      <div style={{ marginTop: 14 }}>
        {isMobile ? (
          <div
            style={{
              fontWeight: 900,
              border: LAYOUT === 'classic' ? '1px solid #999' : '3px solid black',
              padding: '10px 12px',
              background: tableHeaderBg,
              color: tableHeaderColor,
              textTransform: 'uppercase',
              letterSpacing: LAYOUT === 'bold' ? 1 : 0,
            }}
          >
            LINE ITEMS
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              fontWeight: 900,
              border: LAYOUT === 'classic' ? '1px solid #999' : '3px solid black',
              padding: '10px 12px',
              background: tableHeaderBg,
              color: tableHeaderColor,
              textTransform: 'uppercase',
              letterSpacing: LAYOUT === 'bold' ? 1 : 0,
            }}
          >
            <div style={{ flex: 1 }}>LINE ITEM</div>
            <div style={{ width: 80, textAlign: 'right' }}>QTY</div>
            <div style={{ width: 160, flexShrink: 0, textAlign: 'right' }}>RATE</div>
            <div style={{ width: 170, flexShrink: 0, textAlign: 'right' }}>AMOUNT</div>
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          {items.filter(i => i?.type !== 'note').map((it, idx) => {
            const qty = Number(it.qty || 1);
            const rate = Number(it.rate || 0);
            const lineTotal = qty * rate;

            if (isMobile) {
              return (
                <div
                  key={it.id || idx}
                  style={{
                    marginBottom: 8,
                    padding: LAYOUT === 'classic' ? '8px 6px' : '10px 8px',
                    borderBottom: LAYOUT === 'bold'
                      ? `3px solid ${branding.secondary_color}`
                      : LAYOUT === 'classic'
                        ? '1px solid #ddd'
                        : '1px solid rgba(0,0,0,0.35)',
                  }}
                >
                  <div style={{ minWidth: 0, fontWeight: LAYOUT === 'classic' ? 700 : 900, overflowWrap: 'anywhere' }}>{it.description || ''}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '6px 16px', marginTop: 6, fontWeight: LAYOUT === 'classic' ? 700 : 900, fontSize: 11 }}>
                    <div style={{ maxWidth: '100%', whiteSpace: 'nowrap' }}>{qty} × {money(rate)}</div>
                    <div style={{ maxWidth: '100%', marginLeft: 'auto', textAlign: 'right', whiteSpace: 'nowrap' }}>{money(lineTotal)}</div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={it.id || idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginBottom: 8,
                  padding: LAYOUT === 'classic' ? '8px 6px' : '10px 8px',
                  borderBottom: LAYOUT === 'bold'
                    ? `3px solid ${branding.secondary_color}`
                    : LAYOUT === 'classic'
                      ? '1px solid #ddd'
                      : '1px solid rgba(0,0,0,0.35)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>{it.description || ''}</div>
                <div style={{ width: 80, textAlign: 'right', fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>{qty}</div>
                <div style={{ width: 160, flexShrink: 0, textAlign: 'right', fontWeight: LAYOUT === 'classic' ? 700 : 900, fontSize: 11, whiteSpace: 'nowrap' }}>{money(rate)}</div>
                <div style={{ width: 170, flexShrink: 0, textAlign: 'right', fontWeight: LAYOUT === 'classic' ? 700 : 900, fontSize: 11, whiteSpace: 'nowrap' }}>{money(lineTotal)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {notes.length > 0 && (
        <>
          {divider()}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 6, textTransform: LAYOUT === 'bold' ? 'uppercase' : 'none', letterSpacing: LAYOUT === 'bold' ? 1 : 0 }}>
              {LAYOUT === 'classic' ? 'Notes' : 'NOTES'}
            </div>
            {notes.map((n, i) => (
              <div key={n.id || i} style={{ fontWeight: LAYOUT === 'classic' ? 700 : 900, marginBottom: 6, fontStyle: LAYOUT === 'classic' ? 'italic' : 'normal' }}>
                {n.description}
              </div>
            ))}
          </div>
        </>
      )}

      {divider(LAYOUT === 'bold')}

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: '100%', maxWidth: 360 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
            <div>Subtotal</div><div style={{ whiteSpace: 'nowrap' }}>{money(subtotal)}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
            <div>Discount</div><div style={{ whiteSpace: 'nowrap' }}>{money(-discount)}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
            <div>State Tax</div><div style={{ whiteSpace: 'nowrap' }}>{money(stateTaxAmount)}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: LAYOUT === 'classic' ? 700 : 900 }}>
            <div>County/Local Tax</div><div style={{ whiteSpace: 'nowrap' }}>{money(countyTaxAmount)}</div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 900,
              fontSize: LAYOUT === 'bold' ? 22 : 18,
              marginTop: 10,
              paddingTop: 10,
              borderTop: LAYOUT === 'bold'
                ? `8px solid ${branding.primary_color}`
                : LAYOUT === 'classic'
                  ? '2px solid #666'
                  : `6px solid ${branding.primary_color}`,
              color: branding.primary_color,
              textTransform: 'uppercase',
              letterSpacing: LAYOUT === 'bold' ? 2 : 0
            }}
          >
            <div>Total</div><div style={{ whiteSpace: 'nowrap' }}>{money(total)}</div>
          </div>
        </div>
      </div>

      <BusinessInfoBlock />
    </div>
  );
};

export default InvoicePrint;
